import '@shared/ui/styles/ui.css';
import './rootPage.css';

import MobileBottomNavigation from '@features/components/Navigation/MobileBottomNavigation';
import SideNavigation from '@features/components/Navigation/SideNavigation';
import TopNavigation from '@features/components/Navigation/TopNavigation';
import useNavigateGuard from '@features/components/Navigation/useNavigateGuard';
import NotificationProvider from '@features/components/Notification/NotificationProvider';
import GlobalConfirmPrompt from '@features/components/Prompt/GlobalConfirmPrompt.tsx';
import { ServiceWorkerUpdateNotification } from '@features/components/PWA/ServiceWorkerUpdateNotification';
import LogoSvg from '@icons/logo.svg?react';
import { useDeviceDetector } from '@shared/hooks/useDeviceDetector';
import { useDynamicThemeColor } from '@shared/hooks/useDynamicThemeColor';
import { usePWAMode } from '@shared/hooks/usePWAMode';
import Footer from '@shared/ui/Footer/Footer';
import LoadingScreen from '@shared/ui/LoadingScreen/LoadingScreen';
import useFullHeight from '@shared/utils/hooks/useFullHeight';
import { useAppDispatch, useAppSelector } from '@store/hooks.ts';
import { selectNavigatorState, setContentHeight, setPwaMode } from '@store/reducers/appSlice';
import { selectAuthUser } from '@store/reducers/authSlice';
import { useStatisticSitesControllerCreateMutation } from '@store/services/statisticSitesApi';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigation } from 'react-router';

import AuroralBgGradientImg from '../assets/auroral-bg-gradient.webp';

const RootPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigation = useNavigation();
  const location = useLocation();
  const userData = useAppSelector(selectAuthUser);

  const showNavigator = useAppSelector(selectNavigatorState);

  const { fetchingUser } = useNavigateGuard();
  const deviceDetector = useDeviceDetector();

  const { upperContainerRef, lowerContainerRef, height } = useFullHeight({
    bottomPadding: 45,
    shouldListenToResize: deviceDetector.isBigScreen,
  });

  // Initialize PWA mode detection and communication with service worker
  const pwaMode = usePWAMode();

  const [postSiteView] = useStatisticSitesControllerCreateMutation();

  // Initialize dynamic theme color updates for PWA (works by side effect)
  useDynamicThemeColor();

  // Debug PWA mode detection
  useEffect(() => {
    dispatch(
      setPwaMode({
        isPWA: pwaMode.isPWA,
        isInstalled: pwaMode.isInstalled,
        displayMode: pwaMode.displayMode,
        isStandalone: pwaMode.isStandalone,
        isBrowser: pwaMode.isBrowser,
      }),
    );
  }, [pwaMode]);

  useEffect(() => {
    if (deviceDetector.isBigScreen) {
      dispatch(setContentHeight(height));
    }
  }, [height, deviceDetector.isBigScreen]);

  // Add a detection when page path changes, scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);

    if (!location.pathname.includes('admin')) {
      (async () => {
        await postSiteView({
          createSiteStatisticDto: {
            statsDate: new Date().toISOString(),
            url: location.pathname,
          },
        });
      })();
    }
  }, [location.pathname]);

  return (
    <div className="flex flex-col flex-1 main-app relative bg-base-200 max-h-screen h-screen xl:max-h-full xl:h-full overflow-y-hidden overflow-x-hidden xl:overflow-y-auto">
      {deviceDetector.isBigScreen && (
        <TopNavigation ref={upperContainerRef} userData={userData} fetchingUser={fetchingUser} />
      )}

      {/* Main content area */}
      <main
        className={classNames('flex h-full', {
          'w-full max-w-480 gap-2 mx-auto': deviceDetector.isBigScreen,
        })}
      >
        {deviceDetector.isBigScreen && <SideNavigation />}
        <div
          className={classNames('flex flex-col flex-1 transition', {
            'pt-24 pb-10': deviceDetector.isBigScreen,
          })}
        >
          <AnimatePresence mode="sync">
            <div className="relative z-10 flex flex-col flex-1">
              <Outlet />
            </div>
          </AnimatePresence>

          {/* Tablet and Mobile Navigation */}
          <AnimatePresence mode="sync">
            {!deviceDetector.isBigScreen && showNavigator && (
              <MobileBottomNavigation showLabelOnActive />
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Footer for big screens */}
      {deviceDetector.isBigScreen && <Footer ref={lowerContainerRef} />}

      <div className="fixed top-0 right-0 h-full w-full bg-base-200/95 dark:bg-base-200 z-2" />

      <div
        className="fixed top-0 right-0 h-full w-full bg-cover z-1"
        style={{ backgroundImage: `url(${AuroralBgGradientImg})` }}
      />
      <GlobalConfirmPrompt />
      <NotificationProvider />
      <ServiceWorkerUpdateNotification />
      <LoadingScreen
        visible={fetchingUser && navigation.state === 'idle'}
        spinner={<LogoSvg className="fill-current h-8 w-8" />}
        fullScreen={true}
        loadingText={t('settings.app.name')}
      />
    </div>
  );
};

export default RootPage;
